import type { EmployeeState } from './employee-selectors';
import { FAILURE, STARTING, SUCCESS } from '../action/util/action-name-creators';
import { EMPLOYEE_STATUS } from '../employees/actions/load-org-employees-by-status';

import {
  Action,
  StateStatus,
  updateStatusById,
  updateResourceByParam,
  setStateDirtyByParam,
  updateResourceByObjectKey,
  updateStatus,
} from './utils/state-manager';

const InitialState: EmployeeState = {
  employeesById: {},
  employeesStateById: {},
  employeesStateByOrgIdAndStatus: {},
  endEmploymentReasons: null,
  endEmploymentReasonsStatus: StateStatus.notAttempted,
  employeeNotificationsByOrgId: {},
  employeeNotificationsByOrgIdStatus: {},
  employeeSummaryByOrgId: {},
  employeeSummaryStateByOrgId: {},
};

export default (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_ORG_EMPLOYEES_BY_STATUS'): {
      const { orgId, employeeStatus } = action.payload.args;
      return updateResourceByParam(
        state,
        'employees',
        'orgIdAndStatus',
        `${orgId}-${employeeStatus}`,
        true,
      );
    }
    case SUCCESS('LOAD_ORG_EMPLOYEES_BY_STATUS'): {
      const {
        args: { orgId, employeeStatus },
        data,
      } = action.payload;
      const employeesById = data.reduce(
        (obj: { [employeeId: string]: Employee }, e: Employee) => ({ ...obj, [e.employeeId]: e }),
        {},
      );
      return updateResourceByParam(
        state,
        'employees',
        'orgIdAndStatus',
        `${orgId}-${employeeStatus}`,
        false,
        employeesById,
      );
    }

    case STARTING('CREATE_MERGE_EMPLOYEE'):
    case STARTING('LOAD_EMPLOYEE'): {
      const { employeeId } = action.payload.args;
      return updateResourceByParam(state, 'employees', 'id', employeeId, true);
    }
    case SUCCESS('REASSIGN_EMPLOYEE_GROUPS'): {
      const { orgId } = action.payload;
      const newState = setStateDirtyByParam(
        state,
        'employees',
        'orgIdAndStatus',
        `${orgId}-${EMPLOYEE_STATUS.CURRENT}`,
      );
      return setStateDirtyByParam(
        newState,
        'employees',
        'orgIdAndStatus',
        `${orgId}-${EMPLOYEE_STATUS.FORMER}`,
      );
    }
    case 'SET_ORG_EMPLOYEES_BY_STATUS_DIRTY': {
      const { orgId, employeeStatus } = action.payload;
      return setStateDirtyByParam(
        state,
        'employees',
        'orgIdAndStatus',
        `${orgId}-${employeeStatus}`,
      );
    }
    case SUCCESS('CREATE_MERGE_EMPLOYEE'):
    case SUCCESS('UPDATE_EMPLOYEE'):
    case SUCCESS('LOAD_EMPLOYEE'): {
      const {
        args: { employeeId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'employees', 'id', employeeId, false, {
        [employeeId]: data,
      });
    }
    case SUCCESS('DELETE_EMPLOYEE'): {
      const { employeeId, orgId } = action.payload.args;
      const newEmployeesById = { ...state.employeesById };
      delete newEmployeesById[employeeId];

      return {
        ...state,
        employeesById: newEmployeesById,
        employeeNotificationsByOrgIdStatus: {
          ...state.employeeNotificationsByOrgIdStatus,
          [orgId]: StateStatus.dirty,
        },
      };
    }
    case SUCCESS('ADD_EMPLOYEE'): {
      const { data } = action.payload;
      return updateResourceByParam(state, 'employees', 'id', data.employeeId, false, {
        [data.employeeId]: data,
      });
    }
    case SUCCESS('GRANT_EMPLOYEE_ACCESS'): {
      const {
        args: { employeeId },
        data: { user },
      } = action.payload;
      if (!user) {
        return state;
      }
      const newEmployee = { ...state.employeesById[employeeId], username: user.username };
      return updateResourceByParam(state, 'employees', 'id', employeeId, false, {
        [employeeId]: newEmployee,
      });
    }
    case STARTING('LOAD_END_EMPLOYMENT_REASONS'): {
      return updateStatus(state, 'endEmploymentReasons', true);
    }
    case SUCCESS('LOAD_END_EMPLOYMENT_REASONS'): {
      const { data } = action.payload;
      return updateStatus(state, 'endEmploymentReasons', false, data);
    }
    case STARTING('LOAD_EMPLOYEE_NOTIFICATIONS'): {
      const { orgId } = action.payload.args;

      return updateStatusById(state, 'employeeNotificationsByOrgId', true, orgId);
    }
    case SUCCESS('LOAD_EMPLOYEE_NOTIFICATIONS'): {
      const { orgId } = action.payload.args;

      return updateStatusById(
        state,
        'employeeNotificationsByOrgId',
        false,
        orgId,
        action.payload.data,
      );
    }
    case FAILURE('LOAD_EMPLOYEE_NOTIFICATIONS'): {
      const { orgId } = action.payload.args;

      return updateStatusById(state, 'employeeNotificationsByOrgId', false, orgId);
    }
    case STARTING('UPDATE_EMPLOYEE_NOTIFICATION'): {
      const { orgId, username, isOn } = action.payload.args;

      const notificationsByOrgId = state.employeeNotificationsByOrgId[orgId] || [];
      const newNotifications = notificationsByOrgId.slice();

      if (isOn) {
        const existing = newNotifications.find((n) => n.username === username);
        if (!existing) {
          newNotifications.push({
            orgId,
            username,
          });
        }
      } else {
        const existingIndex = newNotifications.findIndex((n) => n.username === username);
        if (existingIndex !== -1) {
          newNotifications.splice(existingIndex, 1);
        }
      }

      return {
        ...state,
        employeeNotificationsByOrgId: {
          ...state.employeeNotificationsByOrgId,
          [orgId]: newNotifications,
        },
      };
    }
    case SUCCESS('MERGE_EMPLOYEES'): {
      const { employeeToKeep, employeeToKeepId, employeeToRemoveId } = action.payload.data;
      const newEmployeesById = { ...state.employeesById, [employeeToKeepId]: employeeToKeep };
      delete newEmployeesById[employeeToRemoveId];

      return {
        ...state,
        employeesById: newEmployeesById,
      };
    }
    case STARTING('ORG_EMPLOYEE_SUMMARY'): {
      const { orgId } = action.payload.args;
      return updateResourceByObjectKey(state, 'employeeSummaryByOrgId', orgId, true);
    }
    case SUCCESS('ORG_EMPLOYEE_SUMMARY'): {
      const {
        data,
        args: { orgId },
      } = action.payload;
      return updateResourceByObjectKey(state, 'employeeSummaryByOrgId', orgId, false, {
        [orgId]: data,
      });
    }
    case FAILURE('ORG_EMPLOYEE_SUMMARY'): {
      const { orgId } = action.payload.args;
      return updateResourceByObjectKey(state, 'employeeSummaryByOrgId', orgId, false);
    }
    case 'SET_ORG_EMPLOYEE_SUMMARY_DIRTY': {
      const { orgId } = action.payload;
      return setStateDirtyByParam(state, 'employeeSummary', 'orgId', orgId);
    }
    default:
      return state;
  }
};
